import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Textarea, TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { AssistantService } from '@core/assistant/assistant.service';
import { AssistantHistoryService } from '@core/assistant/assistant-history.service';
import { LanguageService } from '@core/services/language.service';
import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { AssistantMode } from '@core/assistant/assistant.types';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { injectIsMobile } from '@shared/utils/viewport';
import { AssistantProposalCardComponent } from '@shared/components/assistant-proposal-card/assistant-proposal-card.component';

const WIDTH_STORAGE_KEY = 'speaknode.assistant.panel.width';
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 420;

interface ToolCallView {
  toolCallId: string;
  icon: string;
  label: string;
  /** Чем этот вызов отличается от соседнего такого же — id, путь, поле. */
  argSummary: string;
  failed: boolean;
  running: boolean;
}

/** Раскрытое тело тул-колла: чем звали и что ответили. */
interface ToolCallBody {
  args: string;
  result: string;
  failed: boolean;
  truncated: boolean;
}

/** Длиннее — обрезаем: транскрипт на сотню килобайт положит DOM. */
const TOOL_BODY_LIMIT = 8000;

/**
 * Правая док-панель агента — то, что открывается кнопкой «Ask agent».
 *
 * Панель не плавает поверх приложения: она занимает свою колонку в шелле,
 * а основная зона ужимается. Так агент и пользователь смотрят на одну и ту
 * же страницу, и подсвеченное поле не оказывается под чатом.
 */
@Component({
  selector: 'app-assistant-panel',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    SelectButtonModule,
    TextareaModule,
    TooltipModule,
    ProgressSpinnerModule,
    DeleteConfirmationComponent,
    AssistantProposalCardComponent,
  ],
  templateUrl: './assistant-panel.component.html',
  styleUrl: './assistant-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Закрытая панель — нулевой ширины: так открытие и закрытие можно
    // анимировать, а раскладка рядом едет вместе с ней, в один такт.
    '[style.width.px]': 'ui.panelOpen() ? width() : 0',
    '[class.resizing]': 'resizing()',
    '[class.peek]': "sheet() === 'peek'",
  },
})
export class AssistantPanelComponent {
  private readonly document = inject(DOCUMENT);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  readonly assistant = inject(AssistantService);
  readonly ui = inject(AssistantUiService);
  readonly history = inject(AssistantHistoryService);
  private readonly language = inject(LanguageService);

  /** Открыт список прошлых переписок вместо ленты сообщений. */
  readonly showHistory = signal(false);

  /** Что показывать в истории: пустые разговоры туда не попадают. */
  readonly historyItems = computed(() =>
    this.history.conversations().filter(chat => chat.messageCount > 0),
  );

  private readonly isMobile = injectIsMobile();
  /** Ручное переключение шита перебивает автоматику до закрытия панели. */
  private readonly manualSheet = signal<'full' | 'peek' | null>(null);

  /**
   * На мобиле панель — лист-шит с двумя состояниями. Пока агент ждёт
   * решения, шит сам опускается до половины экрана: подтверждать правку,
   * не видя подсвеченного поля, — ровно то, от чего мы уходили.
   */
  readonly sheet = computed<'full' | 'peek'>(() => {
    if (!this.isMobile()) return 'full';
    return this.manualSheet() ?? (this.ui.hasPending() ? 'peek' : 'full');
  });

  readonly inputText = signal('');
  readonly showClearDialog = signal(false);
  /** Раскрытые тул-коллы — видно аргументы и ответ. */
  readonly expandedCalls = signal<ReadonlySet<string>>(new Set<string>());
  private readonly bodyCache = new Map<string, ToolCallBody>();
  readonly width = signal(this.loadWidth());
  readonly resizing = signal(false);

  private readonly scrollViewport = viewChild<ElementRef<HTMLElement>>('scrollViewport');
  private readonly input = viewChild(Textarea);

  readonly modeOptions: { label: string; value: AssistantMode; icon: string }[] = [
    { label: $localize`:@@assistant.mode.confirm:Ask me`, value: 'confirm', icon: 'pi pi-check-square' },
    { label: $localize`:@@assistant.mode.auto:Auto`, value: 'auto', icon: 'pi pi-bolt' },
  ];

  readonly clearDialogTitle = $localize`:@@assistant.clear.title:Clear chat history`;
  readonly clearDialogSubtitle = $localize`:@@assistant.clear.allSubtitle:Every saved chat will be removed from this browser. This action cannot be undone.`;

  readonly busy = computed(() => this.assistant.busy());
  readonly canSend = computed(() => this.assistant.ready() && !this.busy() && this.inputText().trim().length > 0);

  /** Предложения, которых пользователь ещё не касался. */
  readonly pending = this.ui.pendingProposals;

  /**
   * На десктопе полная карточка с диффом живёт у самого поля, а в панели
   * остаётся строка-напоминание. На мобиле наоборот: у поля только
   * подсветка, решать — здесь, под большим пальцем.
   */
  readonly compactCards = computed(() => !this.isMobile());

  readonly suggestions: string[] = [
    $localize`:@@assistant.suggest.reviewPrompt:Review my agent's last sessions and improve its system prompt`,
    $localize`:@@assistant.suggest.firstMessage:Make the first message shorter and warmer`,
    $localize`:@@assistant.suggest.runTests:Run the evaluation tests for this agent`,
  ];

  /** Переменная, которой панель сообщает документу свою ширину. */
  private static readonly WIDTH_VAR = '--sn-assistant-width';

  /**
   * Класс-признак «режим агента» на <html>.
   *
   * Ширины мало, когда нужно не сместить элемент, а поменять ему набор
   * правил: у нуля нет способа выразить «агент закрыт» в селекторе. Класс
   * это умеет — и, в отличие от переменной, достаёт до узлов вне нашего
   * поддерева: маску drawer'а PrimeNG вешает прямо в <body>.
   *
   * Тем же приёмом тема ставит `.p-dark`, так что место знакомое.
   */
  private static readonly MODE_CLASS = 'sn-assistant-open';

  constructor() {
    // Панель — колонка шелла, и платформа при её открытии ужимается. Но
    // элементы с `position: fixed` внутри страниц якорятся к ВЬЮПОРТУ и
    // про это ужатие не знают: превью виджета на agent-edit оставалось
    // прижатым к правому краю окна и уезжало ПОД чат.
    //
    // CSS-переменная тут потому, что наследованием до них не добраться:
    // превью живёт в другом поддереве, а не внутри панели. Пишем ширину
    // на <html> — оттуда её видит кто угодно:
    //   right: calc(20px + var(--sn-assistant-width, 0px))
    // Закрытая панель нулевой ширины, так что отдельного флага «открыта»
    // не нужно, и превью едет в такт с анимацией.
    effect(() => {
      const open = this.ui.panelOpen();
      const root = this.document.documentElement;
      root.style.setProperty(
        AssistantPanelComponent.WIDTH_VAR,
        `${open ? this.width() : 0}px`,
      );
      root.classList.toggle(AssistantPanelComponent.MODE_CLASS, open);
    });

    // Панель рендерится только когда ассистент доступен: уходя, убираем
    // за собой, иначе переменная останется висеть с последней шириной, а
    // класс — включённым, и drawer'ы будут ужиматься под несуществующую
    // панель.
    this.destroyRef.onDestroy(() => {
      this.document.documentElement.style.removeProperty(
        AssistantPanelComponent.WIDTH_VAR,
      );
      this.document.documentElement.classList.remove(
        AssistantPanelComponent.MODE_CLASS,
      );
    });

    // Держим ленту у нижнего края: rAF ждёт, пока Angular дорисует
    // сообщение, иначе scrollTop выставляется до роста scrollHeight.
    effect(() => {
      this.assistant.messages();
      this.assistant.busy();
      this.ui.pendingProposals();
      requestAnimationFrame(() => this.scrollToBottom());
    });

    // Поле меряет себя само (autoResize) — по своей ширине. А ширина у
    // него меняется не только при вводе: панель открывается анимацией с
    // нуля и тянется мышью за грань. Первый замер приходился на нулевую
    // ширину: плейсхолдер переносился на десяток строк, и поле
    // открывалось во всю высоту. Пересчитываем на каждое изменение
    // ширины панели.
    const host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
    const observer = new ResizeObserver(() => this.input()?.resize());
    observer.observe(host);
    this.destroyRef.onDestroy(() => observer.disconnect());

    this.destroyRef.onDestroy(() => this.stopResize());
  }

  // ------------------------------------------------------------------ input

  send(): void {
    const text = this.inputText();
    if (!text.trim() || this.busy()) return;
    this.assistant.send(text);
    this.inputText.set('');
  }

  useSuggestion(text: string): void {
    this.inputText.set(text);
    this.send();
  }

  onInputKeydown(e: KeyboardEvent): void {
    // Enter — отправка, Shift+Enter — перенос строки.
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      this.send();
    }
  }

  stop(): void {
    this.assistant.stop();
  }

  close(): void {
    this.ui.panelOpen.set(false);
    this.manualSheet.set(null);
  }

  /** Потянуть шит вверх/вниз руками. */
  toggleSheet(): void {
    this.manualSheet.set(this.sheet() === 'full' ? 'peek' : 'full');
  }

  readonly sheetToggleLabel = $localize`:@@assistant.sheet.toggle:Expand or collapse the agent panel`;

  setMode(mode: AssistantMode | null): void {
    if (mode) this.ui.setMode(mode);
  }

  askClearHistory(): void {
    this.showClearDialog.set(true);
  }

  // ---------------------------------------------------------------- история

  toggleHistory(): void {
    this.showHistory.update(v => !v);
  }

  /** Новый разговор: прежний остаётся в истории, экран очищается. */
  newChat(): void {
    this.assistant.startNew();
    this.showHistory.set(false);
    this.inputText.set('');
  }

  openConversation(id: string): void {
    this.assistant.openConversation(id);
    this.showHistory.set(false);
  }

  removeConversation(id: string, event: Event): void {
    event.stopPropagation();
    this.assistant.removeConversation(id);
  }

  /**
   * «2 месяца назад» вместо даты: в списке из десяти строк человек ищет
   * не число, а «то, что вчера», и относительное время читается быстрее.
   */
  when(timestamp: number): string {
    const seconds = Math.round((timestamp - Date.now()) / 1000);
    if (Math.abs(seconds) < 60) return this.justNow;
    const format = new Intl.RelativeTimeFormat(this.language.currentLang() || 'en', { numeric: 'auto' });
    const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
      ['year', 60 * 60 * 24 * 365],
      ['month', 60 * 60 * 24 * 30],
      ['week', 60 * 60 * 24 * 7],
      ['day', 60 * 60 * 24],
      ['hour', 60 * 60],
      ['minute', 60],
    ];
    for (const [unit, size] of steps) {
      if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
    }
    return this.justNow;
  }

  private readonly justNow = $localize`:@@assistant.history.justNow:just now`;

  onClearConfirm(): void {
    this.assistant.forgetAll();
    this.showClearDialog.set(false);
    this.showHistory.set(false);
  }

  // ----------------------------------------------------------------- resize

  startResize(event: PointerEvent): void {
    event.preventDefault();
    this.resizing.set(true);
    const startX = event.clientX;
    const startWidth = this.width();

    const move = (e: PointerEvent) => {
      // Панель прибита к правому краю — тянем влево, ширина растёт.
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (startX - e.clientX)));
      this.width.set(next);
    };
    const up = () => {
      this.stopResize();
      try {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(this.width()));
      } catch {
        // приватный режим
      }
    };
    this.pointerMove = move;
    this.pointerUp = up;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  }

  private pointerMove: ((e: PointerEvent) => void) | null = null;
  private pointerUp: (() => void) | null = null;

  private stopResize(): void {
    this.resizing.set(false);
    if (this.pointerMove) window.removeEventListener('pointermove', this.pointerMove);
    if (this.pointerUp) window.removeEventListener('pointerup', this.pointerUp);
    this.pointerMove = null;
    this.pointerUp = null;
  }

  private loadWidth(): number {
    const raw = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
    if (!Number.isFinite(raw) || raw <= 0) return DEFAULT_WIDTH;
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, raw));
  }

  // --------------------------------------------------------------- rendering

  contentToString(content: unknown): string {
    if (typeof content === 'string') return content;
    if (content == null) return '';
    return JSON.stringify(content);
  }

  renderMarkdown(content: unknown): SafeHtml {
    const text = this.contentToString(content);
    // gfm — таблицы и ~strike~, breaks — одиночный \n превращается в <br>:
    // модели любят писать списки без пустой строки между пунктами.
    const html = marked.parse(text, { async: false, gfm: true, breaks: true }) as string;
    // Результаты тулов содержат пользовательские данные (имена агентов,
    // URL вебхуков) — санитайзим всегда.
    const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  isAutoContinue(message: unknown): boolean {
    return this.assistant.isAutoContinue(message as never);
  }

  /** Человеческое описание тул-колла: «Открываю /agents/…», а не имя функции. */
  toolCallView(call: {
    toolCallId: string;
    name: string;
    status: string;
    args?: unknown;
    result?: PromiseSettledResult<unknown>;
  }): ToolCallView {
    const args = (call.args ?? {}) as Record<string, unknown>;
    const running = call.status === 'pending';
    const failed = this.isToolCallFailed(call);
    let icon = 'pi pi-database';
    let label: string;

    switch (call.name) {
      // Аргументы приезжают потоком и в момент отрисовки могут быть
      // ещё пустыми — подпись без них не должна обрываться на полуслове.
      case 'navigateTo':
        icon = 'pi pi-compass';
        label = args['path']
          ? $localize`:@@assistant.tool.navigate:Opening ${String(args['path'])}:path:`
          : $localize`:@@assistant.tool.navigating:Opening a page`;
        break;
      case 'proposeFieldChange':
        icon = 'pi pi-pencil';
        label = args['fieldId']
          ? $localize`:@@assistant.tool.propose:Editing ${String(args['fieldId'])}:field:`
          : $localize`:@@assistant.tool.proposing:Editing a field`;
        break;
      case 'runPageAction':
        icon = 'pi pi-play';
        label = args['actionId']
          ? $localize`:@@assistant.tool.action:Running ${String(args['actionId'])}:action:`
          : $localize`:@@assistant.tool.acting:Running a page action`;
        break;
      case 'getPageFields':
      case 'getPageActions':
      case 'getCurrentPageContext':
      case 'getAppRoutes':
        icon = 'pi pi-eye';
        label = $localize`:@@assistant.tool.inspect:Looking at the page`;
        break;
      case 'updatePlan':
        icon = 'pi pi-list-check';
        label = $localize`:@@assistant.tool.plan:Updating the plan`;
        break;
      default:
        label = $localize`:@@assistant.tool.read:Reading ${call.name}:tool:`;
    }
    return {
      toolCallId: call.toolCallId,
      icon,
      label,
      argSummary: this.summarizeArgs(call.name, args),
      failed,
      running,
    };
  }

  isExpanded(toolCallId: string): boolean {
    return this.expandedCalls().has(toolCallId);
  }

  toggleCall(toolCallId: string): void {
    this.expandedCalls.update(set => {
      const next = new Set(set);
      if (!next.delete(toolCallId)) next.add(toolCallId);
      return next;
    });
  }

  /**
   * Тело вызова считаем один раз на (вызов + статус) и кладём в кэш:
   * шаблон перечитывает его на каждый change detection, а JSON.stringify
   * транскрипта — это заметные миллисекунды на каждый чих.
   */
  toolCallBody(call: {
    toolCallId: string;
    status: string;
    args?: unknown;
    result?: PromiseSettledResult<unknown>;
  }): ToolCallBody {
    const key = `${call.toolCallId}:${call.status}`;
    const cached = this.bodyCache.get(key);
    if (cached) return cached;

    const args = pretty(call.args ?? {});
    let result = '';
    if (call.status === 'done' && call.result) {
      result =
        call.result.status === 'rejected'
          ? stringifyUnknown(call.result.reason)
          : unwrapToolResult(call.result.value);
    }

    const truncated = args.length + result.length > TOOL_BODY_LIMIT;
    const body: ToolCallBody = {
      args: clip(args, TOOL_BODY_LIMIT / 4),
      result: clip(result, TOOL_BODY_LIMIT),
      failed: this.isToolCallFailed(call),
      truncated,
    };
    this.bodyCache.set(key, body);
    return body;
  }

  /** Отличительная часть аргументов для заголовка — без неё шесть одинаковых строк. */
  private summarizeArgs(name: string, args: Record<string, unknown>): string {
    // Для этих тулов отличие уже вынесено в сам заголовок.
    if (name === 'navigateTo' || name === 'proposeFieldChange' || name === 'runPageAction') return '';
    const parts: string[] = [];
    for (const [k, v] of Object.entries(args)) {
      if (v === null || v === undefined || v === '') continue;
      if (typeof v === 'object') continue;
      parts.push(`${k}=${String(v)}`);
      if (parts.join(' · ').length > 56) break;
    }
    return clip(parts.join(' · '), 56);
  }

  /**
   * Вызов считается неудачным, если промис отклонён ИЛИ обработчик вернул
   * конверт `{ ok: false }` / `{ error }` — так отвечают UI-тулы на отказ
   * пользователя.
   */
  isToolCallFailed(call: { status: string; result?: PromiseSettledResult<unknown> }): boolean {
    if (call.status !== 'done' || !call.result) return false;
    if (call.result.status === 'rejected') return true;
    const v = call.result.value as { error?: unknown; ok?: unknown } | null | undefined;
    return !!v && typeof v === 'object' && (v.error != null || v.ok === false);
  }

  private scrollToBottom(): void {
    const el = this.scrollViewport()?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
}

function clip(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit)}\n…`;
}

function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    // циклические ссылки в результате тула — показываем хоть что-то
    return String(value);
  }
}

function stringifyUnknown(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  return typeof value === 'string' ? value : pretty(value);
}

/**
 * MCP отдаёт ответ конвертом `{ content: [{ type: 'text', text: '<json>' }] }`.
 * Показывать пользователю сам конверт незачем — достаём текст и, если внутри
 * JSON, разворачиваем его читаемо. UI-тулы отвечают обычным объектом.
 */
function unwrapToolResult(value: unknown): string {
  const envelope = value as { content?: Array<{ type?: string; text?: string }> } | null;
  if (envelope && Array.isArray(envelope.content)) {
    const text = envelope.content
      .filter(c => c?.type === 'text' && typeof c.text === 'string')
      .map(c => c.text as string)
      .join('\n');
    if (text) {
      try {
        return JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        return text;
      }
    }
  }
  return pretty(value);
}
